import { WaitlistAlert } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/notifications.pb';
import dayjs from 'dayjs';
import { dispatchTopBarTraySelectionEvent } from '@frontend/shared';
import { CalendarViewOffices, OfficeCalendarView, WorkingHourInfo } from '../components/calendar-view/types';
import { CALENDAR_SCROLL_TO_OFFSET_IN_MINUTES, DEFAULT_CALENDAR_SCROLL_TO_TIME } from '../constants';

const TIME_FORMAT = 'hh:mm A';

const getWorkingHoursStart = (workingHours?: WorkingHourInfo[]) => {
  if (!workingHours?.length) return;

  // Find the earliest start time from all working hours
  const earliestHour = workingHours.reduce((earliest, current) => {
    if (!earliest) return current.startTime;
    return earliest;
  }, workingHours[0]?.startTime);

  return earliestHour;
};

const getWorkingHoursEnd = (workingHours?: WorkingHourInfo[]) => {
  if (!workingHours?.length) return null;

  // Find the latest end time from all working hours
  let latestEndHour: dayjs.Dayjs | null = null;

  for (const workHour of workingHours) {
    const startTime = parseTimeString(workHour.startTime);
    const endTime = startTime.add(workHour.duration, 'minute');

    if (!latestEndHour || endTime.isAfter(latestEndHour)) {
      latestEndHour = endTime;
    }
  }

  return latestEndHour ? latestEndHour.format(TIME_FORMAT) : null;
};

const getEarliestAppointmentTime = (officeData: OfficeCalendarView) => {
  if (!officeData.columnEntities?.length) return;

  let earliestTime = '';

  for (const entity of officeData.columnEntities) {
    if (!entity.events?.length) continue;

    for (const event of entity.events) {
      if (event.appointmentType === 'APPOINTMENT' && event.startHour) {
        if (!earliestTime) {
          earliestTime = event.startHour;
        } else {
          const currentTime = parseTimeString(event.startHour);
          const earliest = parseTimeString(earliestTime);
          if (currentTime.isBefore(earliest)) {
            earliestTime = event.startHour;
          }
        }
      }
    }
  }

  return earliestTime;
};

const parseTimeString = (timeStr: string): dayjs.Dayjs => {
  let time = dayjs(timeStr, 'hh:mm A');
  if (!time.isValid()) time = dayjs(timeStr, 'HH:mm');
  if (!time.isValid()) time = dayjs(timeStr, 'h:mm A');
  return time;
};

const subtractMinutesFromTime = (timeStr: string, minutes: number): string => {
  const time = parseTimeString(timeStr);
  return time.subtract(minutes, 'minute').format(TIME_FORMAT);
};

const calculateScrollPositionForTime = (targetTime: string, pxPerMinute: number): number => {
  const time = parseTimeString(targetTime);

  if (!time.isValid()) {
    throw new Error(`Invalid time format: ${targetTime}`);
  }

  const targetHour = time.hour();
  const targetMinute = time.minute();
  const totalMinutesFromStart = targetHour * 60 + targetMinute;

  return totalMinutesFromStart * pxPerMinute;
};

const scrollTo = (containerElement: HTMLElement | null, scrollPosition: number): void => {
  if (!containerElement) return;

  containerElement.scrollTo({
    top: scrollPosition,
    behavior: 'smooth',
  });
};

const scrollToTime = (containerElement: HTMLElement | null, targetTime: string, pxPerMinute: number) => {
  if (!containerElement) return;

  try {
    const scrollPosition = calculateScrollPositionForTime(targetTime, pxPerMinute);
    scrollTo(containerElement, scrollPosition);
  } catch (error) {
    console.error('Failed to scroll to time:', error);
  }
};

const scrollToCenteredElement = (
  containerElement: HTMLElement,
  element: HTMLElement,
  officeEnd: string | null,
  pxPerMinute: number
): void => {
  const elRect = element.getBoundingClientRect();
  const containerRect = containerElement.getBoundingClientRect();
  // Calculate element's position relative to the scrollable container
  const elRelativeTop = elRect.top - containerRect.top + containerElement.scrollTop;
  // Calculate center position: element position - half of container height
  const containerHeight = containerElement.clientHeight;
  const centerPosition = elRelativeTop - containerHeight / 2;

  if (officeEnd) {
    const officeEndPlusBuffer = parseTimeString(officeEnd)
      .add(CALENDAR_SCROLL_TO_OFFSET_IN_MINUTES, 'minute')
      .format(TIME_FORMAT);
    const officeEndScrollPosition = calculateScrollPositionForTime(officeEndPlusBuffer, pxPerMinute);
    const maxAllowedScroll = officeEndScrollPosition - containerHeight;
    scrollTo(containerElement, Math.min(centerPosition, maxAllowedScroll));
  } else {
    scrollTo(containerElement, centerPosition);
  }
};

export const scrollToPosition = (
  containerElement: HTMLElement | null,
  calendarData: CalendarViewOffices,
  pxPerMinute: number
): void => {
  if (!containerElement || !calendarData?.length) return;

  try {
    // Consider only the first location's timezone for scrolling logic, the first location on the calendar is in the same order as the data array
    const firstOffice = calendarData[0];
    const officeTimezone = firstOffice?.officeTimezone;
    const workingHours = firstOffice.workingHours ?? [];
    const officeStart = getWorkingHoursStart(workingHours); // hh:mm A
    const officeEnd = getWorkingHoursEnd(workingHours); // hh:mm A
    const currentTimeObj = officeTimezone ? dayjs().tz(officeTimezone) : dayjs().tz(); // TODO: Update this for v2
    const currentTime = currentTimeObj.format(TIME_FORMAT);
    const isTodayDateSelected = dayjs(firstOffice.calendarDateValue).isSame(currentTimeObj, 'day');
    const startTime = officeStart || getEarliestAppointmentTime(firstOffice) || DEFAULT_CALENDAR_SCROLL_TO_TIME;

    if (isTodayDateSelected) {
      const currentTimeParsed = parseTimeString(currentTime);
      const startTimeParsed = parseTimeString(startTime);

      if (currentTimeParsed.isBefore(startTimeParsed)) {
        scrollToTime(containerElement, subtractMinutesFromTime(startTime, 15), pxPerMinute);
      } else {
        const currentTimeElement = document.getElementById('current-time-progress-line');
        if (currentTimeElement) {
          scrollToCenteredElement(containerElement, currentTimeElement, officeEnd, pxPerMinute);
        } else {
          scrollToTime(containerElement, DEFAULT_CALENDAR_SCROLL_TO_TIME, pxPerMinute);
        }
      }
    }
    // For days other than today
    else {
      scrollToTime(
        containerElement,
        subtractMinutesFromTime(startTime, CALENDAR_SCROLL_TO_OFFSET_IN_MINUTES),
        pxPerMinute
      );
    }
  } catch (error) {
    console.error('Failed to execute calendar scroll:', error);
  }
};

export const handleWaitlistAppointmentNotificationClick = (_payload: WaitlistAlert) => {
  dispatchTopBarTraySelectionEvent('quickfill');
};

export const getScheduleRequestEventId = (requestId: string | undefined, openingIndex: number) => {
  return requestId && `${requestId}-${openingIndex}`;
};
